import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'wouter';
import { api } from '../lib/api';
import { WorkloadForecast } from '../components/workload-forecast';
import { orderLabels } from '../lib/format';
import { haptic } from '../lib/telegram';
import {
  CALENDAR_GROUP_LABELS,
  buildCalendarLayout,
  type CalendarBar,
  type CalendarGroup,
} from '../lib/order-calendar';
import type { ListResponse, Order } from '../types';
import { EmptyState, ErrorNotice, LoadingScreen, PageHeader } from '../components/ui';

/** Что делать, когда полосу перетащили на `days` дней вбок. */
type MoveHandler = (bar: CalendarBar, days: number) => void;

/** Ширина одного дня. Меньше — подписи месяцев перестают читаться. */
const DAY_WIDTH = 46;

const MONTH_FORMAT = new Intl.DateTimeFormat('ru-RU', { month: 'long', timeZone: 'UTC' });
const WEEKDAY_FORMAT = new Intl.DateTimeFormat('ru-RU', { weekday: 'short', timeZone: 'UTC' });

/**
 * Календарь заказов: горизонтальная лента дней, каждый заказ — полоса от начала
 * работы до дедлайна. Сверху то, что в работе, ниже очередь, в самом низу
 * отложенное.
 */
export function CalendarPage() {
  const queryClient = useQueryClient();
  const orders = useQuery({
    queryKey: ['orders', 'calendar'],
    queryFn: () => api<ListResponse<Order>>('/orders?active=true&limit=100'),
  });
  const layout = useMemo(() => buildCalendarLayout(orders.data?.items ?? []), [orders.data?.items]);
  const scroller = useRef<HTMLDivElement>(null);

  /**
   * Перенос заказа: полосу тащат, а решение принимает сервер. Он один знает про
   * выходные, отпуск и версию заказа — поэтому здесь только новая дата, а
   * ответ на вопрос «можно ли» приходит оттуда.
   */
  const move = useMutation({
    mutationFn: ({ order, day }: { order: Order; day: string }) =>
      api(`/artist/schedule/orders/${order.id}/move`, {
        method: 'POST',
        body: { version: order.version, plannedStartAt: day },
      }),
    onSuccess: () => {
      haptic('success');
      void queryClient.invalidateQueries({ queryKey: ['orders'] });
      void queryClient.invalidateQueries({ queryKey: ['schedule'] });
    },
  });

  const onMove: MoveHandler = (bar, days) => {
    const day = layout.days[bar.offsetDays + days];

    // За край ленты не переносим: там нет дня, на который можно поставить.
    if (day) move.mutate({ order: bar.order, day: day.toISOString().slice(0, 10) });
  };

  // Лента открывается на сегодняшнем дне: чаще всего смотрят именно его.
  useEffect(() => {
    if (layout.todayOffset === null || !scroller.current) {
      return;
    }

    scroller.current.scrollLeft = Math.max(0, (layout.todayOffset - 1) * DAY_WIDTH);
  }, [layout.todayOffset]);

  if (orders.isPending) return <LoadingScreen label="Строим календарь" />;

  return (
    <div className="schedule-page">
      <PageHeader eyebrow="Планирование" title="Расписание" />
      <nav className="schedule-tabs" aria-label="Режим расписания">
        <Link href="/queue">Очередь</Link>
        <Link href="/calendar" className="is-active" aria-current="page">
          Календарь
        </Link>
      </nav>
      {orders.error && <ErrorNotice error={orders.error} />}
      {move.error && <ErrorNotice error={move.error} />}

      {layout.bars.length === 0 ? (
        <EmptyState
          title="Заказов нет"
          text="Активные заказы появятся здесь полосами до дедлайна."
        />
      ) : (
        <div className="calendar" ref={scroller}>
          <div className="calendar__grid" style={{ width: layout.days.length * DAY_WIDTH }}>
            <div className="calendar__months">
              {monthSpans(layout.days).map((span) => (
                <div
                  key={span.key}
                  className="calendar__month"
                  style={{ width: span.length * DAY_WIDTH }}
                >
                  {span.label}
                </div>
              ))}
            </div>
            <div className="calendar__days">
              {layout.days.map((day, index) => (
                <div
                  key={day.toISOString()}
                  className={`calendar__day ${index === layout.todayOffset ? 'is-today' : ''}`}
                  style={{ width: DAY_WIDTH }}
                >
                  <span>{day.getUTCDate()}</span>
                  <small>{WEEKDAY_FORMAT.format(day)}</small>
                </div>
              ))}
            </div>

            <div className="calendar__rows">
              {layout.todayOffset !== null && (
                <div
                  className="calendar__today-line"
                  style={{ left: layout.todayOffset * DAY_WIDTH }}
                />
              )}
              {layout.bars.map((bar) => (
                <CalendarRow key={bar.order.id} bar={bar} onMove={onMove} />
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="calendar-legend">
        {(Object.keys(CALENDAR_GROUP_LABELS) as CalendarGroup[]).map((group) => (
          <span key={group} className={`calendar-legend__item calendar-legend__item--${group}`}>
            {CALENDAR_GROUP_LABELS[group]}
          </span>
        ))}
      </div>

      {/* Сначала сам календарь, разбор загрузки — под ним. */}
      <WorkloadForecast />
    </div>
  );
}

/** Меньше этого сдвига — палец просто дрогнул, а не потащил заказ. */
const DRAG_THRESHOLD_PX = 6;

/**
 * Полосу можно потащить: художник переносит заказ на день, с которого собирается
 * взяться. День, куда полоса попадёт, проверяет сервер — выходной, отпуск и
 * прошедшую дату он не примет, и календарь вернётся как был.
 */
function CalendarRow({ bar, onMove }: { bar: CalendarBar; onMove: MoveHandler }) {
  const [shift, setShift] = useState(0);
  const origin = useRef<number | null>(null);
  const dragged = useRef(false);

  const finish = (event: React.PointerEvent<HTMLAnchorElement>) => {
    if (origin.current === null) return;

    const days = Math.round(shift / DAY_WIDTH);
    origin.current = null;
    setShift(0);
    event.currentTarget.releasePointerCapture(event.pointerId);

    if (dragged.current && days !== 0) {
      onMove(bar, days);
    }
  };

  return (
    <div className="calendar__row">
      <Link
        href={`/orders/${bar.order.id}`}
        className={`calendar__bar calendar__bar--${bar.group} ${bar.openEnded ? 'is-open' : ''} ${
          shift === 0 ? '' : 'is-dragging'
        }`}
        style={{
          marginLeft: bar.offsetDays * DAY_WIDTH,
          width: bar.lengthDays * DAY_WIDTH,
          transform:
            shift === 0 ? undefined : `translateX(${Math.round(shift / DAY_WIDTH) * DAY_WIDTH}px)`,
        }}
        title={`${bar.order.publicNumber} · ${orderLabels[bar.order.status]}`}
        onPointerDown={(event) => {
          origin.current = event.clientX;
          dragged.current = false;
          event.currentTarget.setPointerCapture(event.pointerId);
        }}
        onPointerMove={(event) => {
          if (origin.current === null) return;
          const delta = event.clientX - origin.current;

          if (Math.abs(delta) > DRAG_THRESHOLD_PX) dragged.current = true;
          if (dragged.current) setShift(delta);
        }}
        onPointerUp={finish}
        onPointerCancel={finish}
        // Перетащили — значит не нажали: открывать карточку после переноса не нужно.
        onClick={(event) => {
          if (dragged.current) event.preventDefault();
        }}
      >
        <span className="calendar__bar-label">
          <strong>{bar.order.title}</strong>
          <span>
            {bar.order.client.displayName}
            {bar.openEnded ? ' · без срока' : ''}
          </span>
        </span>
      </Link>
    </div>
  );
}

/** Подписи месяцев: соседние дни одного месяца объединяются в одну ячейку. */
function monthSpans(days: Date[]): Array<{ key: string; label: string; length: number }> {
  const spans: Array<{ key: string; label: string; length: number }> = [];

  for (const day of days) {
    const key = `${day.getUTCFullYear()}-${day.getUTCMonth()}`;
    const last = spans.at(-1);

    if (last?.key === key) {
      last.length += 1;
      continue;
    }

    spans.push({ key, label: MONTH_FORMAT.format(day), length: 1 });
  }

  return spans;
}
